import { skipToken, useQuery } from '@tanstack/react-query';
import { useRef } from 'react';
import { orpc } from '@/api/rpc/client';
import type { ListAuthStatesResponse, AgentAuthState } from '@erp/rpc';

export interface ScreenshotEntry {
  data: string;
  action?: string;
  timestamp: number;
  /** A2A messageId of the thread entry this frame was captured on (operator path). */
  messageId?: string;
}

export interface SessionMessageEntry {
  id: string;
  /** Stable A2A messageId (operator path) — ties screenshots to this entry. */
  messageId?: string;
  message: string;
  action?: string;
  metadata?: Record<string, unknown>;
  level: 'info' | 'warn' | 'error';
  timestamp: number;
}

export interface UseAgentAuthStatesListOptions {
  organizationId: string | undefined;
  limit?: number;
  offset?: number;
}

export function useAgentAuthStatesList({
  organizationId,
  limit = 100,
  offset = 0,
}: UseAgentAuthStatesListOptions) {
  const query = useQuery({
    ...orpc.agent.listAuthStates.queryOptions({
      input: organizationId ? { organizationId, limit, offset } : skipToken,
    }),
    enabled: Boolean(organizationId),
  });

  const authStates = query.data?.authStates ?? [];
  const pagination = query.data?.pagination;

  return {
    ...query,
    authStates,
    pagination,
  } satisfies typeof query & {
    data?: ListAuthStatesResponse;
    authStates: AgentAuthState[];
    pagination: ListAuthStatesResponse['pagination'] | undefined;
  };
}

export interface UseSessionForAgentRunOptions {
  organizationId: string | undefined;
  agentRunId: string | undefined;
  /** Polling interval in ms while sessionId is null. Default: 2000 */
  refetchInterval?: number | false;
}

export function useSessionForAgentRun({
  organizationId,
  agentRunId,
  refetchInterval = 2000,
}: UseSessionForAgentRunOptions) {
  const query = useQuery({
    ...orpc.agent.getSessionForAgentRun.queryOptions({
      input:
        organizationId && agentRunId
          ? { organizationId, agentRunId }
          : skipToken,
    }),
    enabled: Boolean(organizationId && agentRunId),
    refetchInterval: (query) => {
      // Stop polling once we have a sessionId
      if (query.state.data?.sessionId) {
        return false;
      }
      return refetchInterval;
    },
  });

  return {
    ...query,
    sessionId: query.data?.sessionId ?? null,
  };
}

export interface UseSessionStatusOptions {
  organizationId: string | undefined;
  sessionId: string | undefined;
  /** Polling interval in ms. Set to false to disable. Default: 500 */
  refetchInterval?: number | false;
}

const RECORDING_WAIT_MS = 60_000;

export function useSessionStatus({
  organizationId,
  sessionId,
  refetchInterval = 500,
}: UseSessionStatusOptions) {
  // Track when the session first reached a terminal state so we can keep
  // polling briefly while the recording upload finishes (it's async on the server).
  const completedAtRef = useRef<number | null>(null);

  const query = useQuery({
    ...orpc.agent.getSessionStatus.queryOptions({
      input:
        organizationId && sessionId ? { organizationId, sessionId } : skipToken,
    }),
    enabled: Boolean(organizationId && sessionId),
    refetchInterval: (query) => {
      // Stop polling if the session no longer exists on the server
      if (query.state.error) {
        return false;
      }

      const data = query.state.data;
      const status = data?.status;

      // Expired sessions have no recording to wait for — stop immediately
      if (status === 'expired') {
        return false;
      }

      const isTerminal =
        status === 'completed' || status === 'failed' || status === 'cancelled';

      if (isTerminal) {
        // Record when we first noticed the terminal state
        if (completedAtRef.current === null) {
          completedAtRef.current = Date.now();
        }
        // Stop polling once recording is found or the wait window expires
        if (
          data?.recordingDocumentId ||
          Date.now() - completedAtRef.current > RECORDING_WAIT_MS
        ) {
          return false;
        }
        return 3000; // slow poll waiting for recording upload to finish
      }

      completedAtRef.current = null;
      return refetchInterval;
    },
  });

  return {
    ...query,
    status: query.data?.status,
    latestScreenshot: query.data?.latestScreenshot,
    screenshots: query.data?.screenshots ?? [],
    messages: query.data?.messages ?? [],
    savedAuthStateId: query.data?.savedAuthStateId ?? null,
    savedAuthState: query.data?.savedAuthState ?? null,
    error: query.data?.error,
    completedAt: query.data?.completedAt ?? null,
    recordingDocumentId: query.data?.recordingDocumentId ?? null,
    recordingStoppedAt: query.data?.recordingStoppedAt ?? null,
    isComplete:
      query.data?.status === 'completed' ||
      query.data?.status === 'failed' ||
      query.data?.status === 'cancelled' ||
      query.data?.status === 'expired',
    isSuccess: query.data?.status === 'completed',
    isFailed:
      query.data?.status === 'failed' || query.data?.status === 'expired',
  };
}
